import React, { useCallback, useEffect, useState } from 'react';
import { listPreviewAccounts, createPreviewAccount, deletePreviewAccount } from '../../../api/autopilot';
import './PreviewAccountsSection.scss';

/**
 * 專案設定頁的「預覽測試帳號」登記區塊。
 *
 * AI 修復修完後,系統代打 autopilot_config.previewAuth 的 loginApi 登入、對受影響頁面截 after 圖。
 * 這裡登記的「參數」就是要 POST 給 login API 的 body 欄位(= login API 需要什麼就設什麼),分兩袋:
 *   - params(非機密):account / company / tenantId … 明碼存。
 *   - secretParams(機密):password … 後端加密存,登記後不可回讀。
 * 登入回來的東西(Set-Cookie / body 的 token)由 capture 自動注入 cookie + localStorage,
 * cookie 型 / token 型都通,不用在這裡指定對應。
 *
 * 只在編輯既有專案(有 projectId)時顯示。
 */

// 一組 key-value 列編輯器
function KvEditor({ rows, setRows, secret }) {
  const update = (i, field, val) => setRows(rows.map((r, idx) => (idx === i ? { ...r, [field]: val } : r)));
  const add = () => setRows([...rows, { key: '', value: '' }]);
  const remove = (i) => setRows(rows.filter((_, idx) => idx !== i));
  return (
    <div className="pa-kv">
      {rows.map((r, i) => (
        <div key={i} className="pa-kv-row">
          <input
            className="pa-input"
            placeholder="參數名(如 account)"
            value={r.key}
            onChange={(e) => update(i, 'key', e.target.value)}
          />
          <input
            className="pa-input"
            placeholder={secret ? '值(機密)' : '值'}
            type={secret ? 'password' : 'text'}
            value={r.value}
            onChange={(e) => update(i, 'value', e.target.value)}
          />
          <button type="button" className="pa-icon-btn" onClick={() => remove(i)}>✕</button>
        </div>
      ))}
      <button type="button" className="pa-btn" onClick={add}>＋ 加參數</button>
    </div>
  );
}

function kvToObj(rows) {
  const o = {};
  for (const r of rows) if (r.key.trim()) o[r.key.trim()] = r.value;
  return o;
}

export default function PreviewAccountsSection({ projectId }) {
  const [accounts, setAccounts] = useState([]);
  const [label, setLabel] = useState('');
  const [role, setRole] = useState('');
  const [paramRows, setParamRows] = useState([{ key: 'account', value: '' }]);
  const [secretRows, setSecretRows] = useState([{ key: 'password', value: '' }]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [confirmDelId, setConfirmDelId] = useState(null); // 兩步刪除(避免原生 confirm)

  const load = useCallback(async () => {
    if (!projectId) return;
    const { data } = await listPreviewAccounts(projectId);
    if (data?.accounts) setAccounts(data.accounts);
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    setErr('');
    if (!label.trim()) { setErr('請填角色名稱(label)'); return; }
    setSaving(true);
    const { error } = await createPreviewAccount({
      projectId,
      label: label.trim(),
      role: role.trim() || undefined,
      params: kvToObj(paramRows),
      secretParams: kvToObj(secretRows),
    });
    setSaving(false);
    if (error) { setErr(error); return; }
    setLabel(''); setRole('');
    setParamRows([{ key: 'account', value: '' }]);
    setSecretRows([{ key: 'password', value: '' }]);
    load();
  };

  const del = async (id) => {
    await deletePreviewAccount(id);
    setConfirmDelId(null);
    load();
  };

  if (!projectId) return null;

  return (
    <div className="form-group preview-accounts">
      <label>預覽測試帳號</label>
      <span className="field-hint">
        AI 修復修完後,系統會拿這裡的帳號代打上面 previewAuth 的 loginApi 登入、再對受影響頁面截圖。
        <b>要設哪些參數 = 你的 login API body 需要哪些欄位</b>(通常 account / password;看你後端的 login API)。
        機密的(password)放下面「機密參數」,加密存、登記後不可回讀。
      </span>

      {accounts.length > 0 && (
        <div className="pa-list">
          {accounts.map((a) => (
            <div key={a.id} className="pa-item">
              <span className="pa-item-label">{a.label}</span>
              {a.role && <span className="pa-item-role">({a.role})</span>}
              <span className="pa-item-params">
                {Object.entries(a.params || {}).map(([k, v]) => `${k}=${v}`).join(', ')}
              </span>
              {confirmDelId === a.id ? (
                <span className="pa-confirm">
                  <button type="button" className="pa-btn pa-btn-danger" onClick={() => del(a.id)}>確定刪除</button>
                  <button type="button" className="pa-btn" onClick={() => setConfirmDelId(null)}>取消</button>
                </span>
              ) : (
                <button type="button" className="pa-btn" onClick={() => setConfirmDelId(a.id)}>刪除</button>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="pa-form">
        <div className="pa-form-row">
          <input className="pa-input pa-input--grow2" placeholder="角色名稱,如 部門主管(必填)" value={label} onChange={(e) => setLabel(e.target.value)} />
          <input className="pa-input" placeholder="role(選填)" value={role} onChange={(e) => setRole(e.target.value)} />
        </div>
        <div className="pa-form-sublabel">參數(非機密:account / company…)</div>
        <KvEditor rows={paramRows} setRows={setParamRows} />
        <div className="pa-form-sublabel">機密參數(password…,加密存)</div>
        <KvEditor rows={secretRows} setRows={setSecretRows} secret />
        {err && <div className="date-error">{err}</div>}
        <button type="button" className="pa-btn-primary" onClick={add} disabled={saving}>
          {saving ? '登記中…' : '＋ 登記帳號'}
        </button>
      </div>
    </div>
  );
}
